/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Logger, clientUtil } from "@html-core";
import { DisplayCheckService } from "../../common/display-check.service";

export type RegionParamFormat = {
   x1: number;
   y1: number;
   width: number;
   height: number;
   fbStride: number;
   data: Uint8Array;
};

export type VideoParamFormat = {
   codec: string;
   data: Uint8Array;
   multiTransition: boolean;
};

/**
 * WebglRender is responsible for rendering video frames onto a WebGL canvas.
 *
 * It initializes WebGL context, compiles shaders, and manages textures.
 *
 * It can update the canvas with new video data, handle changes in canvas DPI,
 * and ensure efficient rendering of video frames using WebGL.
 */

export class WebglRender {
   // Default shader sources and WebGL version configuration
   private static readonly DEFAULT_CONFIG = {
      version: "webgl2",
      vertexShaderSource: `
        attribute vec2 xy;
        varying highp vec2 uv;
        void main(void) {
           gl_Position = vec4(xy, 0.0, 1.0);
           // Map vertex coordinates (-1 to +1) to UV coordinates (0 to 1).
           // UV coordinates are Y-flipped relative to vertex coordinates.
           uv = vec2((1.0 + xy.x) / 2.0, (1.0 - xy.y) / 2.0);
        }
      `,
      fragmentShaderSource: `
        varying highp vec2 uv;
        uniform sampler2D texture;
        void main(void) {
           gl_FragColor = texture2D(texture, uv);
        }
      `
   };

   private gl: any = null;
   private texture: any = null;
   private isKeyFrame: boolean = false;
   private videoDecoder: VideoDecoder = null;
   private canvasDPIChanged: boolean = false;
   private screenWidth: number = 0;
   private screenHeight: number = 0;

   constructor(
      private canvas: HTMLCanvasElement,
      private displayCheckService: DisplayCheckService,
      private version: string = WebglRender.DEFAULT_CONFIG.version,
      private vertexShaderSource: string = WebglRender.DEFAULT_CONFIG.vertexShaderSource,
      private fragmentShaderSource: string = WebglRender.DEFAULT_CONFIG.fragmentShaderSource
   ) {
      this.initWebgl();
      this.canvas.addEventListener("webglcontextlost", (event: Event) => {
         Logger.error("WebGL context lost", Logger.DISPLAY);
      });
      this.canvas.addEventListener("webglcontextrestored", (event: Event) => {
         Logger.error("WebGL context restored", Logger.DISPLAY);
      });
   }
   /**
    * Initializes WebGL context and sets up shaders
    */
   public initWebgl(): void {
      this.screenWidth = clientUtil.getScreenWidth() * 2;
      this.screenHeight = this.displayCheckService.getScreenHeightWithoutNotchHeight() * 2;
      // Get WebGL context
      this.gl = this.canvas.getContext(this.version);
      if (!this.gl) {
         throw new Error("Unable to initialize WebGL context");
      }

      // Compile vertex shader
      const vertexShader = this.gl.createShader(this.gl.VERTEX_SHADER);
      this.gl.shaderSource(vertexShader, this.vertexShaderSource);
      this.gl.compileShader(vertexShader);
      if (!this.gl.getShaderParameter(vertexShader, this.gl.COMPILE_STATUS)) {
         throw this.gl.getShaderInfoLog(vertexShader);
      }

      // Compile fragment shader
      const fragmentShader = this.gl.createShader(this.gl.FRAGMENT_SHADER);
      this.gl.shaderSource(fragmentShader, this.fragmentShaderSource);
      this.gl.compileShader(fragmentShader);
      if (!this.gl.getShaderParameter(fragmentShader, this.gl.COMPILE_STATUS)) {
         throw this.gl.getShaderInfoLog(fragmentShader);
      }

      // Create and link shader program
      const shaderProgram = this.gl.createProgram();
      this.gl.attachShader(shaderProgram, vertexShader);
      this.gl.attachShader(shaderProgram, fragmentShader);
      this.gl.linkProgram(shaderProgram);
      if (!this.gl.getProgramParameter(shaderProgram, this.gl.LINK_STATUS)) {
         throw this.gl.getProgramInfoLog(shaderProgram);
      }
      this.gl.useProgram(shaderProgram);

      // Set up vertex buffer
      const vertexBuffer = this.gl.createBuffer();
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, vertexBuffer);
      this.gl.bufferData(
         this.gl.ARRAY_BUFFER,
         new Float32Array([-1.0, -1.0, -1.0, +1.0, +1.0, +1.0, +1.0, -1.0]),
         this.gl.STATIC_DRAW
      );

      // Get location of xy attribute in the shader program
      const xyLocation = this.gl.getAttribLocation(shaderProgram, "xy");
      this.gl.vertexAttribPointer(xyLocation, 2, this.gl.FLOAT, false, 0, 0);
      this.gl.enableVertexAttribArray(xyLocation);
      Logger.info("WebGL initialization completed", Logger.DISPLAY);
   }

   /**
    * Updates the region of the WebGL texture
    * @param {RegionParamFormat} param - Region parameters including coordinates and data
    */
   public updateRegion(param: RegionParamFormat): void {
      if (this.canvasDPIChanged && this.texture) {
         Logger.info("Canvas DPI changed, resetting texture", Logger.DISPLAY);
         // Reset texture if canvas DPI has changed
         this.gl.deleteTexture(this.texture);
         this.texture = null;
         this.canvasDPIChanged = false;
      }
      try {
         if (!this.texture) {
            this.texture = this.gl.createTexture();
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture);
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST);
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST);
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
            this.gl.texStorage2D(this.gl.TEXTURE_2D, 1, this.gl.RGBA8, this.screenWidth, this.screenHeight);
            Logger.info("Texture has been initialized", Logger.DISPLAY);
         }
         this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture);
         this.gl.pixelStorei(this.gl.UNPACK_ROW_LENGTH, param.fbStride / 4);
         this.gl.texSubImage2D(
            this.gl.TEXTURE_2D,
            0,
            param.x1,
            param.y1,
            param.width,
            param.height,
            this.gl.RGBA,
            this.gl.UNSIGNED_BYTE,
            param.data,
            0
         );
      } catch (error) {
         Logger.error(`Failed to update the region of the WebGL texture: ${error}`, Logger.DISPLAY);
      }
   }

   /**
    * Updates the video frame
    * @param {VideoParamFormat} param - Video frame parameters including codec and data
    */
   public updateVideo(param: VideoParamFormat): void {
      this.canvasDPIChanged = false;
      this.isKeyFrame = false;
      if (!this.videoDecoder && !param.multiTransition) {
         Logger.info("Create and configure a new VideoDecoder", Logger.DISPLAY);
         let decoder = new VideoDecoder({
            output: (frame) => {
               if (!this.texture) {
                  this.texture = this.gl.createTexture();
                  this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture);
                  this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST);
                  this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST);
                  this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
                  this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
               }

               this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture);
               this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA, this.gl.UNSIGNED_BYTE, frame);
               frame.close();
            },
            error: (error) => {
               Logger.error(`VideoDecoder couldn't decode data: ${error}`, Logger.DISPLAY);
            }
         });

         decoder.configure({
            codec: param.codec,
            optimizeForLatency: true
         });

         this.videoDecoder = decoder;
         this.isKeyFrame = true;
      }

      if (this.videoDecoder) {
         let chunk = new EncodedVideoChunk({
            type: this.isKeyFrame ? "key" : "delta",
            timestamp: 0,
            duration: 0,
            data: param.data
         });
         this.videoDecoder.decode(chunk);
      }
   }

   /**
    * Finalizes the update and renders the frame
    */
   public updateFinished(): void {
      // Configure and clear the drawing area.
      this.gl.viewport(0, 0, this.gl.drawingBufferWidth, this.gl.drawingBufferHeight);
      this.gl.clearColor(1.0, 0.0, 0.0, 1.0);
      this.gl.clear(this.gl.COLOR_BUFFER_BIT);

      // Draw the frame, will draw using the last bound texture as determined
      // by the most recent decode.
      this.gl.drawArrays(this.gl.TRIANGLE_FAN, 0, 4);
   }

   /**
    * Updates the canvas DPI
    * @param {number} screenWidth - New screen width
    * @param {number} screenHeight - New screen height
    */
   public updateResolution4DPI(screenWidth: number, screenHeight: number): void {
      Logger.info(`Updating canvas DPI to screenWidth: ${screenWidth}, screenHeight: ${screenHeight}`, Logger.DISPLAY);
      this.screenWidth = screenWidth;
      this.screenHeight = screenHeight;
      this.canvasDPIChanged = true;
   }
}
